import {
  BRIDGE_PUSH_EVENT,
  BRIDGE_REPLY_EVENT,
  bridgeParseReply,
  bridgeSerialize,
  type BridgeReply,
  type BridgeWebEventType,
} from '@pintrack/bridge';

/**
 * O lado web da ponte.
 *
 * **O app web continua sendo o app web.** Fora da casca, `inNativeShell` é falso e nada disto
 * roda: o navegador segue no caminho que sempre teve. É essa degradação que permite manter as
 * três versões com uma UI só — o mesmo bundle serve o site e o app, e a casca só acrescenta o
 * que o navegador não sabe fazer.
 */

declare global {
  interface Window {
    ReactNativeWebView?: { postMessage(message: string): void };
  }
}

export const inNativeShell = typeof window !== 'undefined' && window.ReactNativeWebView != null;

/**
 * Um timeout é obrigatório, e é generoso de propósito.
 *
 * Uma requisição sem resposta vaza a promessa **e** a entrada no mapa de pendentes — e a casca
 * pode legitimamente nunca responder: o usuário abriu o seletor de contas do Google e deixou o
 * celular na mesa. 60s é tempo de sobra para qualquer interação humana e curto o bastante para
 * a tela não ficar presa para sempre.
 */
const DEFAULT_TIMEOUT_MS = 60_000;

interface Pending {
  resolve(reply: BridgeReply): void;
  timer: ReturnType<typeof setTimeout>;
}

const pending = new Map<string, Pending>();
let listening = false;

function ensureListening(): void {
  if (listening || typeof window === 'undefined') return;
  listening = true;

  window.addEventListener(BRIDGE_REPLY_EVENT, (event) => {
    const reply = bridgeParseReply((event as CustomEvent<string>).detail);
    if (!reply) return;

    const entry = pending.get(reply.id);
    if (!entry) return;
    pending.delete(reply.id);
    clearTimeout(entry.timer);
    entry.resolve(reply);
  });
}

function nextId(): string {
  // `crypto.randomUUID` não existe em WebView antigo do Android; o fallback não precisa ser
  // criptográfico — o id só correlaciona resposta dentro desta página.
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) return crypto.randomUUID();
  return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

/**
 * `null` quando não há casca — quem chama decide o que fazer no navegador, em vez de receber
 * uma exceção que teria de tratar em todo sítio.
 */
export async function bridgeRequest<TResponse>(
  type: BridgeWebEventType,
  payload: unknown = null,
  timeoutMs: number = DEFAULT_TIMEOUT_MS,
): Promise<{ ok: boolean; payload: TResponse | null; error: string | null } | null> {
  if (!inNativeShell || !window.ReactNativeWebView) return null;

  ensureListening();

  const id = nextId();
  const message = bridgeSerialize({ id, type, payload });
  if (!message) return { ok: false, payload: null, error: 'serialize failed' };

  return new Promise((resolve) => {
    const timer = setTimeout(() => {
      pending.delete(id);
      resolve({ ok: false, payload: null, error: 'timeout' });
    }, timeoutMs);

    pending.set(id, {
      timer,
      resolve: (reply) =>
        resolve({ ok: reply.ok, payload: reply.payload as TResponse | null, error: reply.error }),
    });

    window.ReactNativeWebView?.postMessage(message);
  });
}

/** Mensagens que a casca inicia sozinha — back de hardware, hoje. Devolve o cancelador. */
export function onBridgePush(handler: (payload: unknown) => void): () => void {
  if (typeof window === 'undefined') return () => {};
  const listener = (event: Event) => {
    try {
      handler(JSON.parse((event as CustomEvent<string>).detail));
    } catch {
      /* mensagem malformada da casca não derruba a tela */
    }
  };
  window.addEventListener(BRIDGE_PUSH_EVENT, listener);
  return () => window.removeEventListener(BRIDGE_PUSH_EVENT, listener);
}
